/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { dbRealtime, ref, get } from '../environments/firebase';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas-pro';
import { useAuth } from '../contexts/AuthContext';

interface TimeEntry {
  time: string;
  content: string;
  textStyle?: {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    color?: string;
  };
  images?: string[];
}

interface PravachanData {
  [timestamp: string]: {
    vakta: string;
    topic: string;
    dateRange: {
      start: string;
      end: string;
    };
    currentDate: string;
    timeEntries: TimeEntry[];
    drawing?: Array<{path: string; color: string}>;
    createdAt: number;
  };
}

// Improved SVG Icons with proper TypeScript props
const DownloadIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);

const CalendarIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
  </svg>
);

const MicIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
  </svg>
);

const ClockIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
  </svg>
);

const NoteIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path d="M9 2a2 2 0 00-2 2v8a2 2 0 002 2h6a2 2 0 002-2V6.414A2 2 0 0016.414 5L14 2.586A2 2 0 0012.586 2H9z" />
    <path d="M3 8a2 2 0 012-2v10h8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
  </svg>
);

const DrawingIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
  </svg>
);

export default function PravachanReport() {
  const [data, setData] = useState<PravachanData>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) return;
      
      try {
        const snapshot = await get(ref(dbRealtime, `users/${currentUser.uid}/BrahmnaSange`));
        if (snapshot.exists()) {
          setData(snapshot.val());
        } else {
          console.log('No data found');
          setData({});
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  // Convert object to sorted array by date (newest first)
  const sortedPravachans = Object.entries(data)
    .map(([timestamp, entry]) => ({
      timestamp,
      ...entry,
      dateObj: new Date(parseInt(timestamp))
    }))
    .sort((a, b) => b.dateObj.getTime() - a.dateObj.getTime());

    const sanitizeContent = (content: string): string => {
      // Remove all unsupported CSS color functions
      let sanitized = content
        .replace(/color:\s*oklch\([^)]+\)/gi, 'color: #333333')
        .replace(/background:\s*oklch\([^)]+\)/gi, 'background: transparent')
        .replace(/border-color:\s*oklch\([^)]+\)/gi, 'border-color: #cccccc')
        .replace(/fill:\s*oklch\([^)]+\)/gi, 'fill: currentColor')
        .replace(/stroke:\s*oklch\([^)]+\)/gi, 'stroke: currentColor');
    
      // Convert other color formats to hex
      sanitized = sanitized.replace(/color:\s*rgb\((\d+),\s*(\d+),\s*(\d+)\)/gi, (match, r, g, b) => {
        return `color: #${Number(r).toString(16).padStart(2, '0')}${Number(g).toString(16).padStart(2, '0')}${Number(b).toString(16).padStart(2, '0')}`;
      });
    
      return sanitized;
    };
    
    const downloadSinglePDF = async (pravachan: any) => {
      setIsGeneratingPDF(true);
      
      try {
        const tempDiv = document.createElement('div');
        tempDiv.style.position = 'absolute';
        tempDiv.style.left = '-9999px';
        tempDiv.style.width = '210mm';
        tempDiv.style.padding = '20mm';
        tempDiv.style.fontFamily = 'Arial, sans-serif';
        tempDiv.style.backgroundColor = '#ffffff';
        
        // Create image loading promises
        const imagePromises: Promise<void>[] = [];
        const loadedImages: string[] = [];
    
        // Process images first
        for (const entry of pravachan.timeEntries) {
          if (entry.images && entry.images.length > 0) {
            for (const imgUrl of entry.images) {
              imagePromises.push(new Promise((resolve) => {
                const img = new Image();
                img.crossOrigin = 'Anonymous';
                img.onload = () => {
                  // Convert image to data URL
                  const canvas = document.createElement('canvas');
                  canvas.width = img.naturalWidth;
                  canvas.height = img.naturalHeight;
                  const ctx = canvas.getContext('2d');
                  if (ctx) {
                    ctx.drawImage(img, 0, 0);
                    loadedImages.push(canvas.toDataURL('image/jpeg', 0.8));
                  }
                  resolve();
                };
                img.onerror = () => resolve();
                img.src = imgUrl;
              }));
            }
          }
        }
    
        // Wait for all images to load or fail
        await Promise.all(imagePromises);
    
        let imageIndex = 0;
        const content = `
          <style>
            body {
              font-family: Arial, sans-serif;
              color: #333333;
              background: #ffffff;
              margin: 0;
              padding: 0;
            }
            h1 {
              color: #1a365d;
              font-size: 24px;
              text-align: center;
              margin-bottom: 20px;
            }
            .info-block {
              background: #f8f9fa;
              padding: 12px;
              margin-bottom: 20px;
              border-radius: 4px;
            }
            .entry {
              margin-bottom: 20px;
            }
            .time {
              font-weight: bold;
              color: #2b6cb0;
              margin-bottom: 8px;
            }
            .content {
              padding-left: 10px;
              font-size: 14px;
            }
            .image-container {
              padding-left: 10px;
              margin-top: 8px;
            }
            .pdf-image {
              max-width: 100%;
              max-height: 150px;
              border: 1px solid #e2e8f0;
              margin-right: 8px;
              margin-bottom: 8px;
            }
          </style>
          
          <h1>${pravachan.topic}</h1>
          <div style="text-align: center; margin-bottom: 20px;">
            <div style="color: #4a5568; font-size: 14px;">By ${pravachan.vakta}</div>
            <div style="color: #718096; font-size: 12px;">
              ${pravachan.dateObj.toLocaleDateString('en-US', {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}
            </div>
          </div>
          
          <div class="info-block">
            <div style="display: flex; justify-content: space-between; font-size: 12px; color: #4a5568;">
              <span>Date Range:</span>
              <span>
                ${new Date(pravachan.dateRange.start).toLocaleDateString()} - ${new Date(pravachan.dateRange.end).toLocaleDateString()}
              </span>
            </div>
            <div style="display: flex; justify-content: space-between; font-size: 12px; color: #4a5568; margin-top: 8px;">
              <span>Report Date:</span>
              <span>${new Date(pravachan.currentDate).toLocaleDateString()}</span>
            </div>
          </div>
          
          <div>
            ${pravachan.timeEntries.map((entry: TimeEntry) => {
              const sanitizedContent = sanitizeContent(entry.content);
              return `
                <div class="entry">
                  <div class="time">${entry.time}</div>
                  <div class="content">${sanitizedContent}</div>
                  ${entry.images && entry.images.length > 0 ? `
                    <div class="image-container">
                      ${entry.images.map(() => {
                        const imgSrc = loadedImages[imageIndex++];
                        return imgSrc ? `<img src="${imgSrc}" class="pdf-image" />` : '';
                      }).join('')}
                    </div>
                  ` : ''}
                </div>
              `;
            }).join('')}
          </div>
        `;
    
        tempDiv.innerHTML = content;
        document.body.appendChild(tempDiv);
    
        const canvas = await html2canvas(tempDiv, {
          scale: 2,
          logging: true,
          useCORS: true,
          backgroundColor: '#ffffff',
          ignoreElements: (element) => {
            // Skip any potentially problematic elements
            return element.tagName === 'SCRIPT' || 
                   element.classList.contains('ignore-pdf');
          }
        });
    
        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgWidth = 210;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        pdf.addImage(canvas, 'PNG', 0, 0, imgWidth, imgHeight);

        // Add footer
        const pageHeight = pdf.internal.pageSize.height;
        pdf.setFontSize(10);
        pdf.setTextColor(150);
        pdf.text('Generated via MySatsang.com', pdf.internal.pageSize.width / 2, pageHeight - 10, {
          align: 'center'
        });
        
        pdf.save(`pravachan-${pravachan.topic.substring(0, 20)}.pdf`);
        
        
        document.body.removeChild(tempDiv);
      } catch (error) {
        console.error("PDF Generation Error:", error);
        alert('PDF generation failed. Please try again or contact support.');
      } finally {
        setIsGeneratingPDF(false);
      }
    };
    
    const downloadAllPDF = async () => {
      setIsGeneratingPDF(true);
      
      try {
        const pdf = new jsPDF('p', 'mm', 'a4');
        
        // Cover page
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(28);
        pdf.setTextColor(0, 0, 128);
        pdf.text('Pravachan Notes', 105, 140, { align: 'center' });
        pdf.setFontSize(14);
        pdf.setTextColor(100, 100, 100);
        pdf.text('www.MySatsang.com', 105, 160, { align: 'center' });
        pdf.addPage();
    
        for (let i = 0; i < sortedPravachans.length; i++) {
          const pravachan = sortedPravachans[i];
          const tempDiv = document.createElement('div');
          tempDiv.style.position = 'absolute';
          tempDiv.style.left = '-9999px';
          tempDiv.style.width = '210mm';
          tempDiv.style.padding = '20mm';
          tempDiv.style.fontFamily = 'Arial, sans-serif';
          tempDiv.style.backgroundColor = '#ffffff';
          
          const content = `
            <style>
              .pdf-page {
                font-family: Arial, sans-serif;
                color: #333333;
              }
              .pdf-title {
                color: #000080;
                font-size: 24px;
                text-align: center;
                margin-bottom: 20px;
              }
              .pdf-meta {
                text-align: center;
                margin-bottom: 30px;
              }
              .pdf-speaker {
                color: #555555;
                font-size: 14px;
              }
              .pdf-date {
                color: #777777;
                font-size: 12px;
              }
              .pdf-entry {
                margin-bottom: 20px;
                page-break-inside: avoid;
              }
              .pdf-time {
                font-weight: bold;
                color: #0000ff;
                margin-bottom: 8px;
              }
              .pdf-content {
                padding-left: 10px;
                font-size: 14px;
                line-height: 1.5;
              }
              .pdf-images {
                padding-left: 10px;
                margin-top: 8px;
                display: flex;
                flex-wrap: wrap;
                gap: 10px;
              }
              .pdf-image {
                max-width: 100%;
                max-height: 150px;
                border: 1px solid #dddddd;
                object-fit: contain;
              }
            </style>
            
            <div class="pdf-page">
              <h1 class="pdf-title">${pravachan.topic}</h1>
              <div class="pdf-meta">
                <div class="pdf-speaker">By ${pravachan.vakta}</div>
                <div class="pdf-date">
                  ${pravachan.dateObj.toLocaleDateString('en-US', {
                    weekday: 'short',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </div>
              </div>
              
              <div>
                ${pravachan.timeEntries.map(entry => {
                  const sanitizedContent = sanitizeContent(entry.content);
                  return `
                    <div class="pdf-entry">
                      <div class="pdf-time">${entry.time}</div>
                      <div class="pdf-content">
                        ${sanitizedContent}
                      </div>
                      ${entry.images && entry.images.length > 0 ? `
                        <div class="pdf-images">
                          ${entry.images.map(img => `
                            <img src="${img}" 
                                 class="pdf-image"
                                 onerror="this.remove()"
                                 crossorigin="anonymous" />
                          `).join('')}
                        </div>
                      ` : ''}
                    </div>
                  `;
                }).join('')}
              </div>
            </div>
          `;
    
          tempDiv.innerHTML = content;
          document.body.appendChild(tempDiv);
    
          const images = tempDiv.querySelectorAll('img');
          const imageLoadPromises = Array.from(images).map(img => {
            if (img.complete) return Promise.resolve();
            
            return new Promise<void>((resolve) => {
              img.onload = () => resolve();
              img.onerror = () => {
                img.remove();
                resolve();
              };
            });
          });
    
          await Promise.all(imageLoadPromises);
          await new Promise(resolve => setTimeout(resolve, 500));
    
          const canvas = await html2canvas(tempDiv, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
            ignoreElements: (element) => element.tagName === 'SCRIPT'
          });
    
          const imgWidth = 210;
          const pageHeight = 297;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          
          let heightLeft = imgHeight;
          let position = 0;
          
          pdf.addImage(canvas, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
          
          while (heightLeft >= 0) {
            position = heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(canvas, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
          }
          
          if (i < sortedPravachans.length - 1) {
            pdf.addPage();
          }
          
          document.body.removeChild(tempDiv);
        }
    
        pdf.save('katha-amrut-report.pdf');
      } catch (error) {
        console.error("PDF Generation Error:", error);
        alert('Failed to generate PDF. Please try again.');
      } finally {
        setIsGeneratingPDF(false);
      }
    };
  

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const renderStyledContent = (content: string) => {
    return <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: content }} />;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-blue-800">Pravachan Reports</h1>
            <p className="text-gray-500 text-sm mt-1">Your collection of spiritual discourses</p>
          </div>
          <button
            onClick={downloadAllPDF}
            disabled={isGeneratingPDF || sortedPravachans.length === 0}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 w-full sm:w-auto justify-center"
          >
            {isGeneratingPDF ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generating...
              </>
            ) : (
              <>
                <DownloadIcon className="h-5 w-5" />
                Download All Reports
              </>
            )}
          </button>
        </div>
        
        {sortedPravachans.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <NoteIcon className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No reports found</h3>
            <p className="text-gray-500 max-w-md mx-auto">You haven't created any pravachan reports yet. Start by creating your first report.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {sortedPravachans.map((pravachan) => (
              <div key={pravachan.timestamp} className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 hover:shadow-md transition-shadow">
                <div className="p-5 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="bg-blue-100 p-2 rounded-full">
                          <MicIcon className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">{pravachan.topic}</h2>
                          <div className="flex items-center text-gray-600 text-sm mt-1">
                            <span>By {pravachan.vakta}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
                      <CalendarIcon className="h-4 w-4" />
                      <span>
                        {pravachan.dateObj.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <div className="text-xs font-medium text-blue-800 mb-1">Date Range</div>
                      <div className="text-sm text-blue-600">
                        {new Date(pravachan.dateRange.start).toLocaleDateString()} - {new Date(pravachan.dateRange.end).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="p-3 ">
                     
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg">
                      <div className="text-xs font-medium text-green-800 mb-1">Report Date</div>
                      <div className="text-sm text-green-600">
                      {new Date(pravachan.currentDate).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 border-b pb-2 mb-4">
                      <ClockIcon className="h-5 w-5 text-gray-500" />
                      Vicharan Report
                    </h3>
                    
                    {pravachan.timeEntries.length === 0 ? (
                      <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
                        No session entries recorded
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {pravachan.timeEntries.map((entry, index) => (
                          <div key={index} className="relative pl-8 group">
                            <div className="absolute left-0 top-0.5 w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                              <div className="flex justify-between items-start mb-2">
                                <span className="font-medium text-blue-600">{entry.time}</span>
                              </div>
                              <div className="prose max-w-none text-gray-700">
                                {renderStyledContent(entry.content)}
                              </div>
                              {entry.images && entry.images.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-3">
                                  {entry.images.map((imgUrl, imgIndex) => (
                                    <img 
                                      key={imgIndex}
                                      src={imgUrl}
                                      alt={`Entry ${index} image ${imgIndex}`}
                                      className="max-w-full h-auto max-h-32 rounded border border-gray-200"
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {pravachan.drawing && pravachan.drawing.length > 0 && (
                    <div className="mb-6">
                      <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 border-b pb-2 mb-4">
                        <DrawingIcon className="h-5 w-5 text-gray-500" />
                        Drawings
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <svg className="w-full h-64 border border-gray-200 bg-white rounded">
                          {pravachan.drawing.map((item, index) => (
                            <path 
                              key={index} 
                              d={item.path} 
                              stroke={item.color} 
                              strokeWidth="2"
                              fill="none" 
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          ))}
                        </svg>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end mt-6 pt-4 border-t border-gray-100">
                    <button
                      onClick={() => downloadSinglePDF(pravachan)}
                      disabled={isGeneratingPDF}
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition-colors"
                    >
                      <DownloadIcon className="h-5 w-5" />
                      Download Report
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}